<template>
  <div class="w-full max-w-screen-xl px-4 py-8 mx-auto">
    <div
      class="p-4 mb-6 border-l-4 rounded border-secondary bg-tertiary-200 text-gray-800"
      role="note"
      data-test="sample-banner"
    >
      <p class="font-medium">
        Sample report with illustrative data
      </p>
      <p class="mt-1 text-sm">
        Every name, score and comparison on this page is made up to show what an employer sees after a candidate completes an assessment. It is not a real candidate, a norm or a benchmark, and it is not counted as usage.
      </p>
    </div>

    <BasePageHeader>
      <div>
        <BasePageBreadcrumbs :breadcrumbs="breadcrumbs" />
        <BasePageHeading>
          {{ candidate.firstName }} {{ candidate.surname }}
        </BasePageHeading>
      </div>
      <BasePageActions>
        <span class="mr-2 text-sm text-gray-600">{{ candidate.email }}</span>
      </BasePageActions>
    </BasePageHeader>

    <BaseWrapper>
      <p class="mb-4 text-sm text-gray-600">
        Assessment: <strong>{{ assessmentName }}</strong>. Four timed tests, about {{ minutes }} minutes for a candidate.
      </p>

      <ul class="space-y-6">
        <li
          v-for="(exam, index) in exams"
          :key="exam.slug"
        >
          <BaseCard no-padding>
            <AptitudeExamResults
              :icon="iconFor(exam)"
              :candidate="candidate"
              :exam="exam"
              :index="index"
              :show-graph="false"
            />
          </BaseCard>
        </li>
      </ul>

      <BaseCard class="mt-6">
        <h2 class="text-xl">
          What a full report adds for each test
        </h2>
        <ul class="mt-3 ml-5 space-y-2 list-disc list-outside text-gray-700">
          <li
            v-for="(item, index) in realReportAdds"
            :key="index"
          >
            {{ item }}
          </li>
        </ul>
        <p class="mt-4 text-sm text-gray-600">
          Percentage scores are the share of questions answered correctly. Bands and averages describe other people who took the same test on this platform; they are not a validated norm for a specific job.
        </p>
        <div class="mt-6">
          <BaseButton :to="{ name: 'sign-up' }">
            Try it with your own candidates
          </BaseButton>
          <p class="mt-2 text-xs text-gray-600">
            Free trial, no card needed. Results for your first five candidates are free during the trial.
          </p>
        </div>
      </BaseCard>
    </BaseWrapper>
  </div>
</template>

<script>
import AptitudeExamResults from '@components/Candidates/AptitudeExamResults'
import { sampleCandidate, sampleExams, sampleAssessmentName, realReportAdds } from '@data/sampleReport'
import camelCase from 'lodash/camelCase'

export default {
  components: {
    AptitudeExamResults
  },

  page() {
    return {
      title: 'Sample report',
      meta: [
        { name: 'description', content: `An illustrative ${process.env.VUE_APP_SATELLITE_NAME} candidate report with sample data` },
        { name: 'robots', content: 'noindex' }
      ]
    }
  },

  data() {
    return {
      candidate: sampleCandidate,
      exams: sampleExams,
      assessmentName: sampleAssessmentName,
      realReportAdds
    }
  },

  computed: {
    /**
     * @return {Array}
     */
    breadcrumbs() {
      return [
        { name: 'Sample organisation' },
        { name: 'Assessments' },
        { name: this.assessmentName }
      ]
    },

    /**
     * @return {Number}
     */
    minutes() {
      return this.exams.reduce((sum, exam) => sum + (exam.minutes || 0), 0)
    }
  },

  methods: {
    /**
     * Same icon resolution as a real report card
     *
     * @param {Object} exam
     * @return {Function}
     */
    iconFor(exam) {
      const icons = ['calculator', 'presentation-chart-bar', 'view-grid', 'clipboard-check']
      if (!icons.includes(exam.icon)) {
        return () => import('@components/Icons/Exams/Puzzle')
      }
      let icon = camelCase(exam.icon)
      icon = icon.charAt(0).toUpperCase() + icon.slice(1)
      return () => import('@components/Icons/Exams/' + icon)
    }
  }
}
</script>
